"use client";

import DrawerMenu from "@/app/components/menu/drawerMenu";
import AccountMenuV2, { getAccountDisplayText } from "@/app/layouts/standard/header/accountMenuV2";
import DealsMenu from "@/app/layouts/standard/header/dealsMenu";
import SupportMenu from "@/app/layouts/standard/header/supportMenu";
import HouseBrandMenu, { HOUSE_BRAND_TITLE } from "@/app/new/ec/product/[basePath]/sections/house-brand/houseBrandMenu";
import MoneyBackGuaranteeMenu, {
  MONEY_BACK_GUARANTEE_TITLE,
} from "@/app/new/ec/product/[basePath]/sections/value-props/moneyBackGuaranteeMenu";
import VendorOrderItemsMenu from "@/app/review/vendor/vendorOrderItemsMenu";
import { dismissAllToasts } from "@/components/design-system/toast/toastDismiss";
import { setOpenMenu, type VendorOrderItemsMenuPayload } from "@/store/drawerMenuOpenSlice";
import { useAppDispatch } from "@/store/hooks";
import { setMenuOverlayDarkened } from "@/store/overlayDarkeningSlice";
import { AppState } from "@/store/store";
import { useGetUserNameDetailsQuery, useGetUserQuery } from "@/store/userApi";
import { hideChat, showChat } from "@/store/zendeskNotificationSlice";
import { useWindowWidth } from "@react-hook/window-size";
import { usePathname } from "next/navigation";
import {
  isValidElement,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getOverlayDrawerLayoutVariant, HEADER_LINE_MENU_KEYS, type OverlayMenuType } from "@/types/overlayMenu";
import { useSelector } from "react-redux";
import style from "./overlayMenuSystem.module.scss";

const BOOTSTRAP_SM_BREAKPOINT = 576;

function createVendorOrderItemsOverlayChild(
  payload: VendorOrderItemsMenuPayload,
  closeCallback: () => void,
): ReactElement {
  const title = `All Items Ordered from ${payload.vendorName}`;
  return (
    <VendorOrderItemsMenu
      headerText={title}
      vendorName={payload.vendorName}
      items={payload.items}
      closeCallback={closeCallback}
    />
  );
}

export default function OverlayMenuSystem({ isPdpMvpEnabled }: OverlayMenuSystemProps) {
  const { data: user } = useGetUserQuery();
  const { data: nameData } = useGetUserNameDetailsQuery();
  const pathname = usePathname();
  const windowWidth = useWindowWidth();
  const isDesktop = windowWidth >= BOOTSTRAP_SM_BREAKPOINT;

  const dispatch = useAppDispatch();
  const openMenu: OverlayMenuType = useSelector((state: AppState) => state.drawerMenuOpenSlice.openMenu);
  const vendorOrderItems = useSelector((state: AppState) => state.drawerMenuOpenSlice.vendorOrderItems);
  const isDarkened = useSelector((state: AppState) => state.overlayDarkeningSlice.isDarkened);
  const isZendeskChatHidden = useSelector((state: AppState) => state.zendeskNotificationSlice.chatHidden);
  const [activeMenu, setActiveMenu] = useState<ReactNode | null>(null);
  const [previousMenuStack, setPreviousMenuStack] = useState<ReactElement[]>([]);
  const [headerText, setHeaderText] = useState<string>("");
  const [preserveLowerNav, setPreserveLowerNav] = useState<boolean>(false);
  const [currentMenuKey, setCurrentMenuKey] = useState<OverlayMenuType>("none");
  const [switchingToMenu, setSwitchingToMenu] = useState<OverlayMenuType | null>(null);
  const [closeRequested, setCloseRequested] = useState(false);
  const [shouldRestoreZendeskChat, setShouldRestoreZendeskChat] = useState(false);
  /** After mount, `overHeader` overlay can portal to `document.body` so it stacks above portaled Bootstrap modals (z-index ~1055). */
  const [overlayPortalHostReady, setOverlayPortalHostReady] = useState(false);

  const activeMenuRef = useRef(activeMenu);
  activeMenuRef.current = activeMenu;
  const shouldRestoreZendeskChatRef = useRef(shouldRestoreZendeskChat);
  shouldRestoreZendeskChatRef.current = shouldRestoreZendeskChat;

  useEffect(() => {
    setOverlayPortalHostReady(true);
  }, []);

  // `openMenu` is the single source of truth for the header drawers and for the
  // mobile lower-nav Deals menu, so one effect covers both (SPR-2863).
  useEffect(() => {
    if (openMenu !== "none") {
      dismissAllToasts();
    }
  }, [openMenu]);

  const handleCloseButton = useRef(() => {
    setActiveMenu(null);
    setPreviousMenuStack([]);
    setPreserveLowerNav(false);
    setCurrentMenuKey("none");
    setCloseRequested(false);
    dispatch(setOpenMenu("none"));
    if (shouldRestoreZendeskChatRef.current) {
      dispatch(showChat());
      setShouldRestoreZendeskChat(false);
    }
  });

  const { loginURL, createAccountURL } = useMemo(() => {
    if (pathname) {
      const currentPathForRedirect = encodeURIComponent(pathname);
      const loginURL = "/login?origin=" + currentPathForRedirect;
      const createAccountURL = "/create-account?origin=" + currentPathForRedirect;
      return {
        currentPathForRedirect: currentPathForRedirect,
        loginURL: loginURL,
        createAccountURL: createAccountURL,
      };
    } else {
      return {
        currentPathForRedirect: "",
        loginURL: "/login",
        createAccountURL: "/create-account",
      };
    }
  }, [pathname]);

  const navigateToSubMenuCallback = useCallback(
    (newActiveMenu: ReactElement | null) => {
      if (newActiveMenu && isValidElement(activeMenu)) {
        // Remember the previous menu
        const previousMenuStackCopy: ReactElement[] = previousMenuStack;
        previousMenuStackCopy.unshift(activeMenu);
        setPreviousMenuStack(previousMenuStackCopy);
        // Set the active menu to the one clicked
        const menuProps = newActiveMenu.props as MenuComponentProps;
        setHeaderText(menuProps.headerText);
        setActiveMenu(newActiveMenu);
      }
    },
    [activeMenu, previousMenuStack],
  );
  // Prevent stale state of activeMenu inside of navigateToSubMenuCallback
  const navigateToSubMenuCallbackRef = useRef(navigateToSubMenuCallback);
  // Update the callback ref on re-render
  navigateToSubMenuCallbackRef.current = navigateToSubMenuCallback;

  const handleBackButton = useCallback(() => {
    if (previousMenuStack[0]) {
      // pop the top element off of the previousMenuStack
      const targetMenu = previousMenuStack[0];
      const slicedStack = previousMenuStack.slice(1);
      setPreviousMenuStack(slicedStack);

      //restore the last menu one level up
      setActiveMenu(targetMenu);
      const targetMenuProps = targetMenu.props as MenuComponentProps;
      setHeaderText(targetMenuProps.headerText);
      setPreserveLowerNav(targetMenuProps.preserveLowerNav ?? false);
    }
  }, [previousMenuStack]);

  // Define the top-level menu components in here.
  const topLevelMenus = useMemo<MenuOverlayObject>(() => {
    const result = {
      account: (
        <AccountMenuV2
          navigateToSubMenuCallback={navigateToSubMenuCallbackRef}
          headerText={getAccountDisplayText(nameData)}
          user={user}
          loginURL={loginURL}
          createAccountURL={createAccountURL}
          closeCallback={handleCloseButton.current}
          isPdpMvpEnabled={isPdpMvpEnabled}
        />
      ),
      support: (
        <SupportMenu
          navigateToSubMenuCallback={navigateToSubMenuCallbackRef}
          headerText={"Support"}
          closeCallback={handleCloseButton.current}
        />
      ),
      deals: (
        <DealsMenu
          navigateToSubMenuCallback={navigateToSubMenuCallbackRef}
          headerText={"Deals"}
          closeCallback={handleCloseButton.current}
          preserveLowerNav={true}
        />
      ),
      moneyBackGuarantee: (
        <MoneyBackGuaranteeMenu headerText={MONEY_BACK_GUARANTEE_TITLE} closeCallback={handleCloseButton.current} />
      ),
      houseBrand: <HouseBrandMenu headerText={HOUSE_BRAND_TITLE} closeCallback={handleCloseButton.current} />,
      cart: <>Not Implemented</>,
    };
    return result;
  }, [createAccountURL, loginURL, user, nameData, isPdpMvpEnabled]);

  const onSwitchComplete = useCallback(
    (menu: OverlayMenuType) => {
      if (menu === "vendorOrderItems") {
        if (vendorOrderItems) {
          const title = `All Items Ordered from ${vendorOrderItems.vendorName}`;
          setHeaderText(title);
          setActiveMenu(createVendorOrderItemsOverlayChild(vendorOrderItems, handleCloseButton.current));
          setPreviousMenuStack([]);
          setPreserveLowerNav(false);
          setCurrentMenuKey(menu);
          if (!isZendeskChatHidden) {
            dispatch(hideChat());
            setShouldRestoreZendeskChat(true);
          }
        }
        setSwitchingToMenu(null);
        return;
      }

      const selectedMenu = topLevelMenus[menu as keyof MenuOverlayObject];
      if (selectedMenu) {
        const selectedMenuProps = selectedMenu.props as MenuComponentProps;
        setHeaderText(selectedMenuProps.headerText);
        setActiveMenu(selectedMenu);
        setPreviousMenuStack([]);
        setPreserveLowerNav(selectedMenuProps.preserveLowerNav ?? false);
        setCurrentMenuKey(menu);
        if (!isZendeskChatHidden) {
          dispatch(hideChat());
          setShouldRestoreZendeskChat(true);
        }
      }
      setSwitchingToMenu(null);
    },
    [dispatch, isZendeskChatHidden, topLevelMenus, vendorOrderItems],
  );

  useEffect(() => {
    let selectedMenu: ReactElement | undefined;
    let shouldClose = false;
    switch (openMenu) {
      case "none":
        shouldClose = true;
        break;
      case "account":
        selectedMenu = topLevelMenus["account"];
        break;
      case "support":
        selectedMenu = topLevelMenus["support"];
        break;
      case "deals":
        selectedMenu = topLevelMenus["deals"];
        break;
      case "moneyBackGuarantee":
        selectedMenu = topLevelMenus["moneyBackGuarantee"];
        break;
      case "houseBrand":
        selectedMenu = topLevelMenus["houseBrand"];
        break;
      case "vendorOrderItems":
        if (!vendorOrderItems) {
          shouldClose = true;
          break;
        }
        selectedMenu = createVendorOrderItemsOverlayChild(vendorOrderItems, handleCloseButton.current);
        break;
    }
    if (selectedMenu) {
      if (isDesktop && currentMenuKey !== "none" && openMenu !== currentMenuKey) {
        setSwitchingToMenu(openMenu as OverlayMenuType);
        return;
      }
      const selectedMenuProps = selectedMenu.props as MenuComponentProps;
      setHeaderText(selectedMenuProps.headerText);
      setActiveMenu(selectedMenu);
      setPreviousMenuStack([]);
      setPreserveLowerNav(selectedMenuProps.preserveLowerNav ?? false);
      setCurrentMenuKey(openMenu as OverlayMenuType);
      if (!isZendeskChatHidden) {
        dispatch(hideChat());
        setShouldRestoreZendeskChat(true);
      }
    } else if (shouldClose) {
      if (activeMenuRef.current) {
        setCloseRequested(true);
      } else {
        handleCloseButton.current();
      }
    }
  }, [dispatch, isDesktop, isZendeskChatHidden, currentMenuKey, openMenu, topLevelMenus, vendorOrderItems]);

  // Ignore other menu triggers in outside-click so switching works; do not ignore the active one so clicking it closes with animation
  const menuTriggerIgnoreSelectors = useMemo(() => {
    if (openMenu === "account") return ['[data-menu-trigger="support"]'];
    if (openMenu === "support") return ['[data-menu-trigger="account"]'];
    return ['[data-menu-trigger="account"]', '[data-menu-trigger="support"]'];
  }, [openMenu]);

  // Update darkening state based on whether activeMenu is open
  useEffect(() => {
    dispatch(setMenuOverlayDarkened(!!activeMenu));
  }, [activeMenu, dispatch]);

  const layoutVariant = getOverlayDrawerLayoutVariant(currentMenuKey);

  const dimBackgroundClassName = useMemo(() => {
    const parts: string[] = [style.dimBackground];
    if (preserveLowerNav) {
      parts.push(style.preserveLowerNav);
    }
    if (layoutVariant === "overHeader") {
      parts.push(style.dimHeader);
    }
    return parts.join(" ");
  }, [preserveLowerNav, layoutVariant]);

  const useOverHeaderBodyPortal = overlayPortalHostReady && layoutVariant === "overHeader";

  const overlayMenuLayer = (
    <>
      {isDarkened && <div id="dim-background-v2" className={dimBackgroundClassName} />}
      <DrawerMenu
        mobileMenuClass={style.mobileContainerHeight}
        id="overlay-menu"
        showMenu={!!activeMenu}
        showBackArrow={previousMenuStack.length > 0}
        headerText={headerText}
        onClose={handleCloseButton.current}
        onBack={handleBackButton}
        preserveLowerNav={preserveLowerNav}
        switchState={{
          switchingToMenu,
          onSwitchComplete,
          menuKey: currentMenuKey,
        }}
        closeOptions={{ closeRequested, menuTriggerIgnoreSelectors }}
        layoutVariant={layoutVariant}
        headerLine={HEADER_LINE_MENU_KEYS.has(currentMenuKey)}
      >
        {activeMenu}
      </DrawerMenu>
    </>
  );

  return (
    <div className={style.overlayMenuSystemContainer}>
      {useOverHeaderBodyPortal
        ? createPortal(<div className={style.modalStackOverlayPortal}>{overlayMenuLayer}</div>, document.body)
        : overlayMenuLayer}
    </div>
  );
}

type MenuComponentProps = {
  headerText: string;
  preserveLowerNav?: boolean;
  [key: string]: unknown;
};

type MenuComponent = ReactElement<MenuComponentProps>;

type MenuOverlayObject = Record<string, MenuComponent>;

export type { OverlayMenuType } from "@/types/overlayMenu";

export type OverlayMenuSystemProps = {
  isPdpMvpEnabled: boolean;
};

export type OverlayMenuBaseProps = {
  headerText: string;
  navigateToSubMenuCallback?: RefObject<(newActiveMenu: ReactElement | null) => void>;
  closeCallback?: () => void;
  preserveLowerNav?: boolean;
};
